import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Camera } from 'lucide-react'

import { api } from '@/lib/api/client'

/** The visit's photo, for whoever can read its case or the manager it is held for. */
function useVisitPhoto(visitId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['visits', visitId, 'photo'],
    enabled,
    queryFn: async () => (await api.get<Blob>(`/v1/visits/${visitId}/photo`, { responseType: 'blob' })).data,
    // A visit's photo never changes; a 403/404 will not heal on retry.
    staleTime: Infinity,
    retry: false,
  })
}

/**
 * The photo the agent took at the visit. Collapsed to a "View photo" link
 * unless [open], so a long visit history does not download every photo.
 * [thumbnail] shows it small, for a list of visits; a click opens it full size.
 */
export function VisitPhoto({
  visitId, open = false, thumbnail = false,
}: { visitId: string; open?: boolean; thumbnail?: boolean }) {
  const [shown, setShown] = useState(open)
  const { data, isPending, error } = useVisitPhoto(visitId, shown)
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!data) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(data)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [data])

  if (!shown) {
    return (
      <button
        type="button"
        onClick={() => setShown(true)}
        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-navy-700 underline"
      >
        <Camera className="h-3.5 w-3.5" />
        View photo
      </button>
    )
  }
  const status = isAxiosError(error) ? error.response?.status : undefined
  if (error) {
    return (
      <p className="mt-1 text-xs text-negative">
        {status === 404
          ? 'No photo was taken on this visit.'
          : status === 403
            ? 'This visit is outside the cases you can see, so its photo is not shown.'
            : "Couldn't load the photo. Try again in a moment."}
      </p>
    )
  }
  if (isPending || !url) {
    return <div className={thumbnail ? 'h-24 w-32 animate-pulse rounded bg-slate-100' : 'mt-1 h-40 w-56 animate-pulse rounded bg-slate-100'} />
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" title="Open full size" className="mt-1 block w-fit">
      <img
        src={url}
        alt="Photo taken at the visit"
        className={`${thumbnail ? 'max-h-24 max-w-32' : 'max-h-60'} rounded border border-slate-200 bg-white object-contain`}
      />
    </a>
  )
}
